import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useData } from '../contexts/DataContext';
import { useT } from '../lib/i18n';
import { addDaysIsoDate } from '../lib/invoiceService';
import { displayNumber, isNumberPending } from '../lib/numbering';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function InvoiceEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const { getInvoice, saveInvoice } = useData();
  const { t, lang } = useT();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const inv = await getInvoice(id);
      if (cancelled) return;
      if (!inv) {
        setLoading(false);
        return;
      }
      setForm({
        ...inv,
        invoice_date: inv.invoice_date || '',
        due_date: inv.due_date || '',
        notes: inv.notes || '',
        conditions: inv.conditions || '',
        site_address: inv.site_address || '',
        client_name: inv.client_name || '',
        client_address: inv.client_address || '',
        client_phone: inv.client_phone || '',
        client_email: inv.client_email || '',
        status: inv.status || 'draft',
      });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id, getInvoice]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onInvoiceDateChange = (value) => {
    update({
      invoice_date: value,
      due_date: value ? addDaysIsoDate(value, 30) : form.due_date,
    });
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    try {
      const saved = await saveInvoice(form);
      toast.success(lang === 'fr' ? 'Facture enregistrée' : 'Invoice saved');
      nav(`/invoices/${saved.id}`, { replace: true });
    } catch {
      toast.error(t('invoice.convert_failed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>
      </Layout>
    );
  }

  if (!form) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500">
          {lang === 'fr' ? 'Facture introuvable' : 'Invoice not found'}
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={t('invoice.edit')}>
      <Link to={`/invoices/${id}`} className="inline-flex items-center gap-1 text-sm font-bold text-gray-600 mb-4">
        <ArrowLeft size={16} /> {t('auth.back')}
      </Link>

      <h1 className="font-heading text-3xl font-black tracking-tighter mb-1">{t('invoice.edit')}</h1>
      <div className={`font-mono text-sm mb-6 ${isNumberPending(form.invoice_number) ? 'italic text-amber-600' : 'text-gray-500'}`}>
        {displayNumber(form.invoice_number, t)}
      </div>

      <form onSubmit={onSave} className="space-y-4" data-testid="invoice-edit-form">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold mb-1.5">{t('invoice.date')}</label>
            <input
              type="date"
              className="field"
              value={form.invoice_date}
              onChange={(e) => onInvoiceDateChange(e.target.value)}
              data-testid="invoice-date-input"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1.5">{t('invoice.due_date')}</label>
            <input
              type="date"
              className="field"
              value={form.due_date}
              onChange={(e) => update({ due_date: e.target.value })}
              data-testid="invoice-due-date-input"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1.5">
            {lang === 'fr' ? 'Statut' : 'Status'}
          </label>
          <select
            className="field"
            value={form.status}
            onChange={(e) => update({ status: e.target.value })}
            data-testid="invoice-status-select"
          >
            <option value="draft">{t('invoice.status.draft')}</option>
            <option value="sent">{t('invoice.status.sent')}</option>
            <option value="paid">{t('invoice.status.paid')}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1.5">
            {lang === 'fr' ? 'Client' : 'Client'}
          </label>
          <input
            className="field"
            value={form.client_name}
            onChange={(e) => update({ client_name: e.target.value })}
            data-testid="invoice-client-name"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1.5">{t('invoice.site_address')}</label>
          <input
            className="field"
            value={form.site_address}
            onChange={(e) => update({ site_address: e.target.value })}
            data-testid="invoice-site-address"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1.5">{t('invoice.notes')}</label>
          <textarea
            className="field min-h-[100px] py-3"
            value={form.notes}
            onChange={(e) => update({ notes: e.target.value })}
            data-testid="invoice-notes"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1.5">{t('invoice.conditions')}</label>
          <textarea
            className="field min-h-[100px] py-3"
            value={form.conditions}
            onChange={(e) => update({ conditions: e.target.value })}
            data-testid="invoice-conditions"
          />
        </div>

        <p className="text-xs text-gray-500">
          {lang === 'fr'
            ? 'Les lignes matériaux / main-d’œuvre restent celles de la conversion. Modifier la facture ne change pas la soumission d’origine.'
            : 'Material / labour lines stay as converted. Editing the invoice does not change the original quote.'}
        </p>

        <button type="submit" className="btn-primary w-full" disabled={saving} data-testid="invoice-save-btn">
          {saving ? <Loader2 className="animate-spin" size={18} /> : t('invoice.save')}
        </button>
      </form>
    </Layout>
  );
}
